// ============================================================
// src/services/registration-data.service.ts
// Changements :
// Collection Firestore : 'registrationData'
// Document ID : '{userId}_{role}' (ex: 'abc123_buyer')
// ============================================================

import {
  doc, getDoc, getDocs, setDoc,
  collection, query, where,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';

// ID de document déterministe : userId_role
const docId = (userId: string, role: string) => `${userId}_${role}`;

/**
 * Sauvegarde les données d'inscription dans Firestore.
 * Utilise setDoc avec merge:true pour gérer les ré-inscriptions.
 */
export async function saveRegistrationData(
  userId: string,
  role: string,
  data: Record<string, unknown>
): Promise<void> {
  try {
    // Retirer les champs sensibles avant la sauvegarde
    const sanitizedData = { ...data };
    delete sanitizedData.password;
    delete sanitizedData.confirmPassword;
    delete sanitizedData.motDePasse;
    delete sanitizedData.confirmMotDePasse;
    delete sanitizedData.profilePhoto; // Les objets File ne sont pas sérialisables

    await setDoc(
      doc(db, 'registrationData', docId(userId, role)),
      {
        userId,
        role,
        data: sanitizedData,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (err) {
    console.error('Failed to save registration data:', err);
  }
}

/**
 * Charge les données d'inscription pour un utilisateur et un rôle.
 */
export async function loadRegistrationData(
  userId: string,
  role: string
): Promise<Record<string, unknown> | null> {
  try {
    const snap = await getDoc(doc(db, 'registrationData', docId(userId, role)));
    if (!snap.exists()) return null;
    return snap.data().data as Record<string, unknown> ?? null;
  } catch (err) {
    console.error('Failed to load registration data:', err);
    return null;
  }
}

/**
 * Charge toutes les données d'inscription pour un utilisateur (tous rôles).
 */
export async function loadAllRegistrationData(
  userId: string
): Promise<Array<{ role: string; data: Record<string, unknown> }>> {
  try {
    const snap = await getDocs(
      query(collection(db, 'registrationData'), where('userId', '==', userId))
    );
    return snap.docs.map(d => ({
      role: d.data().role as string,
      data: d.data().data as Record<string, unknown>,
    }));
  } catch (err) {
    console.error('Failed to load all registration data:', err);
    return [];
  }
}

/**
 * Met à jour les données d'inscription en fusionnant avec l'existant.
 */
export async function updateRegistrationData(
  userId: string,
  role: string,
  updates: Record<string, unknown>
): Promise<boolean> {
  try {
    const existing = await loadRegistrationData(userId, role);
    const mergedData = { ...(existing || {}), ...updates };

    await setDoc(
      doc(db, 'registrationData', docId(userId, role)),
      {
        userId,
        role,
        data: mergedData,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    return true;
  } catch (err) {
    console.error('Failed to update registration data:', err);
    return false;
  }
}